import React, { FC, ReactElement } from 'react';

import PropTypes from 'prop-types';
import { ErrorBoundary } from 'react-error-boundary';

import { REPORT_TYPES, report } from '@adsk/exo-skeleton-api';
import { RemoteProvider } from '@adsk/exo-skeleton-react';

import LazyComponent, { LazyComponentProps } from './LazyComponent';
import { EXO_SKELETON_SCOPE_NAME } from '../../consts';
import { useRemoteIsVisible } from '../../hooks/useRemoteIsVisible';
import { REPORT_EVENT_TYPES } from '../../utils/monitoring';
import { getRemoteRuntime } from '../../utils/runtime';
import AppContentError from '../AppContentError/AppContentError';

export type RemoteComponentProps = Omit<LazyComponentProps, 'url'> & {
  renderError?: () => ReactElement;
};
const defaultRenderError = () => <AppContentError />;

const RemoteComponent: FC<RemoteComponentProps> = ({
  renderError = defaultRenderError,
  children,
  ...props
}) => {
  const { scope, module } = props;

  const { moniker, override } = getRemoteRuntime(scope) || {};

  useRemoteIsVisible({ scope, module });

  return (
    <ErrorBoundary
      fallbackRender={() => renderError()}
      resetKeys={[module, scope, children, renderError]}
      onError={(err) => {
        return report({
          scope: EXO_SKELETON_SCOPE_NAME,
          type: REPORT_TYPES.error,
          value: err,
          context: {},
          UNSAFE__globalContext: {
            type: REPORT_EVENT_TYPES.REMOTE_MODULE_ERROR,
            moniker,
            override,
            scope,
            module,
          },
        });
      }}
    >
      <RemoteProvider scope={scope} moduleName={module}>
        <LazyComponent {...props}>{children}</LazyComponent>
      </RemoteProvider>
    </ErrorBoundary>
  );
};

RemoteComponent.propTypes = {
  module: PropTypes.string.isRequired,
  scope: PropTypes.string.isRequired,
  renderLoader: PropTypes.func,
  children: PropTypes.node,
  renderError: PropTypes.func,
};

export default RemoteComponent;
